import React, { useEffect, useState } from 'react';
import { BanknotesIcon, CalendarIcon, ChevronLeftIcon, CreditCardIcon } from '@heroicons/react/16/solid';
import { Link, useNavigate, useParams } from 'react-router-dom'; // useParams to get route params
import { Heading, Subheading } from '../../../../components/Heading';
import { Badge } from '../../../../components/Badge';
import { Button } from '../../../../components/Button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../../../components/Description-List';
import { Card } from '../../../../components/Card';
import { Avatar } from '../../../../components/Avatar';
import { UserService } from '../../../../services/userService'; // Import the UserService
import Loading from '../../../../components/Loading';
import dateFormat from 'dateformat';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { WarehouseProductCategory } from '../../../../models/WarehouseProductCategory';
import { useWarehouseProductCategory } from '../../../../hooks/useWarehouseProductCategory';

const ViewWarehouseProductCategoryIdPage: React.FC = () => {
    const { id } = useParams<{ id: string }>(); // Use useParams to get the id from the URL
    const navigate = useNavigate();
    const [category, setCategory] = useState<WarehouseProductCategory | null>(null);
    const { fetchCategoryById, loading, error } = useWarehouseProductCategory();

    useEffect(() => {
        const fetchCategoryDetails = async () => {
            if (!id) return; // Ensure ID is valid before making the request

            try {
                const fetchedCategory = await fetchCategoryById(Number(id));
                setCategory(fetchedCategory);
            } catch (err) {
                showErrorToast('Failed to fetch category details.');
            }
        };
        fetchCategoryDetails();
    }, [id]);

    useEffect(() => {
        document.title = "View Product Category";
    }, []);

    if (loading) return <Loading />;
    if (error) return <p className='py-16 text-center text-red-700'>{error}</p>;
    if (!category) return <p className='py-16 text-center'>Category not found.</p>;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/inventories/categories" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    Product Categories
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex items-center gap-4">
                    <Avatar square initials={category.category_name![0]} className="bg-zinc-300 text-gray-700 size-8" />
                    <Heading>{category.category_name}</Heading>
                </div>
                <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
                    <div className="flex flex-wrap gap-x-10 gap-y-4 py-1.5">
                        <span className="flex items-center gap-2 text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                            <CalendarIcon className="size-4 shrink-0 fill-zinc-400 dark:fill-zinc-500" />
                            <span>{dateFormat(category.created_time, "mmmm dd, yyyy")}</span>
                        </span>
                    </div>
                    <div className="flex gap-4">
                        <Button onClick={() => navigate(`/gp/inventories/categories/edit/${category.id}`)}>Edit</Button>
                    </div>
                </div>
            </div>
            <Card className="mt-12 p-6">
                <Subheading>Producy Category Information</Subheading>
                <DescriptionList className="mt-4">
                    <DescriptionTerm>Category Name</DescriptionTerm>
                    <DescriptionDetails>{category.category_name}</DescriptionDetails>
                    <DescriptionTerm>Category Code</DescriptionTerm>
                    <DescriptionDetails>{category.category_code}</DescriptionDetails>
                    <DescriptionTerm>Department</DescriptionTerm>
                    <DescriptionDetails>{category.department_name}</DescriptionDetails>
                    <DescriptionTerm>Remarks</DescriptionTerm>
                    <DescriptionDetails>{category.remarks || '-'}</DescriptionDetails>
                </DescriptionList>
            </Card>
        </>
    );
};

export default ViewWarehouseProductCategoryIdPage;
